import * as React from "react";
import { Sparkles } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
} from "@eduviaos/ui-kit";
import type { LearnerFormCustomField, LearnerFormResponse } from "@eduviaos/shared";
import { SuggestPermissionsDrawer } from "@/components/SuggestPermissionsDrawer";
import { useSendStaffInvites, useStaffInviteAISuggest } from "@/hooks/useStaffInvites";
import { useTeamForm } from "@/hooks/useTeamForm";
import { useTerms } from "@/hooks/useTerms";

interface InviteTeamMemberSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function fieldOf(form: LearnerFormResponse | undefined, key: string) {
  return form?.fields.find((item) => item.key === key);
}

/**
 * Phase SC-22 — invite one team member from People, using the same
 * fields as the first-run invite and the same AI permission suggestion.
 */
export function InviteTeamMemberSheet({ open, onOpenChange }: InviteTeamMemberSheetProps) {
  const { terms } = useTerms();
  const formQuery = useTeamForm({ enabled: open });
  const send = useSendStaffInvites();
  const suggest = useStaffInviteAISuggest();
  const form = formQuery.data;
  const person = terms.team.one;

  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [designation, setDesignation] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [job, setJob] = React.useState("");
  const [custom, setCustom] = React.useState<Record<string, string>>({});
  const [permissions, setPermissions] = React.useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setFullName("");
      setEmail("");
      setDesignation("");
      setPhone("");
      setJob("");
      setCustom({});
      setPermissions([]);
      setDrawerOpen(false);
    }
  }, [open]);

  const designationField = fieldOf(form, "designation");
  const phoneField = fieldOf(form, "phone");
  const customFields = (form?.custom_fields ?? []).filter((item) => item.visible);
  const nameLabel = fieldOf(form, "full_name")?.label ?? "Full name";
  const emailLabel = fieldOf(form, "email")?.label ?? "Email";

  async function onSuggest() {
    const label = job.trim() || designation.trim() || person;
    try {
      await suggest.mutateAsync({ label, role: "staff" });
      setDrawerOpen(true);
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : "Couldn't suggest access.");
    }
  }

  async function onSend() {
    const customFieldsBody: Record<string, string> = {};
    for (const item of customFields) {
      const value = (custom[item.key] ?? "").trim();
      if (value) customFieldsBody[item.key] = value;
    }
    try {
      const result = await send.mutateAsync([
        {
          email: email.trim(),
          full_name: fullName.trim(),
          role: "staff",
          designation: designationField?.visible === false ? null : designation.trim() || null,
          phone: phoneField?.visible ? phone.trim() || null : null,
          custom_fields: customFieldsBody,
          permission_overrides: permissions,
        },
      ]);
      if (result.sent.length === 0) {
        toast.error(result.skipped[0]?.message ?? "The invite wasn't sent.");
        return;
      }
      toast.success(`Invite sent to ${email.trim()}.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : "Couldn't send the invite.");
    }
  }

  const suggestion = suggest.data;
  const canSend = fullName.trim().length > 0 && email.trim().length > 0 && !send.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Invite a {person.toLowerCase()}</SheetTitle>
          <SheetDescription>
            They&apos;ll get an email to set a password and join your{" "}
            {terms.team.many.toLowerCase()}.
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-3">
          <Field label={nameLabel} value={fullName} onChange={setFullName} required />
          <Field label={emailLabel} value={email} onChange={setEmail} type="email" required />
          {designationField?.visible !== false ? (
            <Field
              label={designationField?.label ?? "Designation"}
              value={designation}
              onChange={setDesignation}
              required={Boolean(designationField?.required)}
            />
          ) : null}
          {phoneField?.visible ? (
            <Field
              label={phoneField.label}
              value={phone}
              onChange={setPhone}
              required={phoneField.required}
            />
          ) : null}
          {customFields.map((item) => (
            <CustomField
              key={item.key}
              field={item}
              value={custom[item.key] ?? ""}
              onChange={(value) => setCustom((prev) => ({ ...prev, [item.key]: value }))}
            />
          ))}
        </div>

        <div className="grid gap-2 rounded-lg border p-3">
          <Label htmlFor="invite-job">What will they do?</Label>
          <Input
            id="invite-job"
            value={job}
            placeholder="Greets learners at the front desk and marks attendance"
            onChange={(event) => setJob(event.target.value)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="justify-self-start"
            onClick={() => void onSuggest()}
            disabled={suggest.isPending}
          >
            <Sparkles className="size-3.5" aria-hidden="true" />
            {suggest.isPending ? "Thinking…" : "Suggest permissions"}
          </Button>
          {permissions.length > 0 ? (
            <p className="text-muted-foreground text-xs">
              {permissions.length} permission{permissions.length === 1 ? "" : "s"} will be granted.
            </p>
          ) : null}
        </div>

        <Button type="button" onClick={() => void onSend()} disabled={!canSend}>
          {send.isPending ? "Sending…" : "Send invite"}
        </Button>
      </SheetContent>

      <SuggestPermissionsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={`Suggest permissions for ${job.trim() || designation.trim() || person}`}
        subtitle="Based on the job you described. Nothing is saved until you send the invite."
        status={
          suggest.isPending
            ? "loading"
            : suggest.isError
              ? "error"
              : suggestion
                ? "success"
                : "idle"
        }
        suggestions={suggestion?.suggested_overrides ?? []}
        rationales={suggestion?.rationales ?? {}}
        ceilingTruncated={suggestion?.ceiling_truncated}
        unknownDropped={suggestion?.unknown_dropped}
        provider={suggestion?.provider}
        model={suggestion?.model}
        fallbackDepth={suggestion?.fallback_depth}
        parseError={suggestion?.parse_error}
        errorMessage={suggest.error instanceof Error ? suggest.error.message : null}
        onRetry={() => void onSuggest()}
        onApply={(selected) => {
          setPermissions(selected);
          setDrawerOpen(false);
        }}
      />
    </Sheet>
  );
}

function Field({
  label,
  value,
  onChange,
  required = false,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
}) {
  const id = `invite-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </Label>
      <Input id={id} type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function CustomField({
  field,
  value,
  onChange,
}: {
  field: LearnerFormCustomField;
  value: string;
  onChange: (value: string) => void;
}) {
  return <Field label={field.label} value={value} onChange={onChange} required={field.required} />;
}
